/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { getSkillStatus } from '../../ia/api/skills';

export const SKILL_TRAINING_STATUS_POLLING_FREQUENCY = {
  HIGH: 2000,
  MEDIUM: 5000,
  LOW: 60000
};

export const enum SkillTrainingStatus {
  TRAINING = 'training',
  NONE = 'none',
  INITIAL = 'initial'
}

class TrainingStatusPollingMachine {
  readonly skillId: string;
  readonly useNewIAUpdatedAccess: boolean;
  readonly assistantId: string;
  private running: boolean;
  private frequency: number;
  private status: SkillTrainingStatus;
  private trainingStatusChangeCallback:
    | null
    | ((_status: SkillTrainingStatus) => void);

  constructor(
    skillId: string,
    useNewIAUpdatedAccess: boolean,
    assistantId: string
  ) {
    this.skillId = skillId;
    this.useNewIAUpdatedAccess = useNewIAUpdatedAccess;
    this.assistantId = assistantId;
    this.running = false;
    this.frequency = SKILL_TRAINING_STATUS_POLLING_FREQUENCY.HIGH;
    this.status = SkillTrainingStatus.INITIAL;
    this.trainingStatusChangeCallback = null;
  }

  async poll() {
    try {
      const result = await getSkillStatus(
        this.skillId,
        this.useNewIAUpdatedAccess,
        this.assistantId
      );
      this.updateTrainingStatus(
        result.training
          ? SkillTrainingStatus.TRAINING
          : SkillTrainingStatus.NONE
      );
      this.updateFrequency(
        result.training
          ? SKILL_TRAINING_STATUS_POLLING_FREQUENCY.HIGH
          : SKILL_TRAINING_STATUS_POLLING_FREQUENCY.MEDIUM
      );
    } catch (e) {
      // slow down polling when we have errors.
      this.updateFrequency(SKILL_TRAINING_STATUS_POLLING_FREQUENCY.LOW);
    }
  }

  async start() {
    this.running = true;
    try {
      while (true) {
        await this.poll();
        await new Promise(resolve => {
          setTimeout(resolve, this.frequency);
        });
        if (!this.running) {
          break;
        }
      }
    } catch (e) {
      // continue to poll even if we have errors.
    }
  }

  stop() {
    this.running = false;
  }

  updateTrainingStatus(status) {
    const isStatusChanged = this.status !== status;
    this.status = status;
    if (
      isStatusChanged &&
      typeof this.trainingStatusChangeCallback === 'function'
    ) {
      this.trainingStatusChangeCallback(this.status);
    }
  }

  updateFrequency(frequency) {
    this.frequency = frequency;
  }

  /**
   *
   * @callback TrainingStatusChange
   * @param {'training' | 'none'} status the training status
   */
  /**
   * add a callback to be triggered when the training status changes.
   * @param {TrainingStatusChange} callback the callback function
   * @returns {void}
   */
  onTrainingStatusChange(callback) {
    this.trainingStatusChangeCallback = callback;
  }
}

export default TrainingStatusPollingMachine;
